import { AuthenticationContext } from '@eneco/dc/provider';
import { DEVICE_ID_COOKIE_AGE, GrowthBook, applyHoldoutGate, getGrowthbookTrackingEvent } from '@eneco/growthbook';
import { UseRouter } from '@eneco/next';
import { Attributes, FeatureDefinitions } from '@growthbook/growthbook-react';
import { Customers_Experiments_ExperimentResponseModel } from '@monorepo-types/dc';
import Cookies from 'js-cookie';

import { MergedConfig, TestConfigVariant } from '../types/experiment';
import { mergeConfigsFromArray } from '../utils/mergeConfigs';

const getConfigs = (
  gb: GrowthBook,
  defaultConfig: MergedConfig,
  testConfigs?: TestConfigVariant[],
  disableExperiments?: boolean,
  forceTestIndex?: number,
) => {
  if (disableExperiments) {
    return defaultConfig;
  }

  if (
    testConfigs &&
    forceTestIndex &&
    !Number.isNaN(forceTestIndex) &&
    forceTestIndex >= 1 &&
    forceTestIndex <= testConfigs.length
  ) {
    const forcedConfig = testConfigs[forceTestIndex - 1];
    return mergeConfigsFromArray(defaultConfig, [forcedConfig]);
  }

  const activeVariants =
    testConfigs?.filter(config => {
      const featureValue = gb.getFeatureValue(config.testKey, '');
      return featureValue === config.variant;
    }) ?? [];

  return mergeConfigsFromArray(defaultConfig, activeVariants);
};

interface ExtendedAttributes extends Attributes {
  disableExperiments?: boolean;
  forceTestIndex?: number;
}

interface GrowthbookInstance {
  growthbook: GrowthBook;
  routes: MergedConfig['routes'];
  actions: MergedConfig['actions'];
  initialContext: MergedConfig['initialContext'];
}

export const createGrowthbookInstance = ({
  data,
  testConfigs,
  defaultConfig,
  router,
  dc,
  attributes,
  pushToDataLayer,
}: {
  data: Customers_Experiments_ExperimentResponseModel[] | null | undefined;
  testConfigs?: TestConfigVariant[];
  defaultConfig: MergedConfig;
  router: UseRouter;
  dc: AuthenticationContext;
  attributes?: ExtendedAttributes;
  pushToDataLayer: (event: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
}): GrowthbookInstance => {
  let combinedFeatures: FeatureDefinitions = {};
  if (Array.isArray(data)) {
    try {
      combinedFeatures = data.reduce<FeatureDefinitions>((acc, item) => {
        if (item?.id && item?.definition) {
          try {
            // Features are keyed by their raw id here, so the holdout prerequisite is referenced
            // by the same raw id (identity resolver) to resolve against the loaded holdout feature.
            acc[item.id] = applyHoldoutGate(JSON.parse(item.definition), item.holdout);
          } catch {
            // Skip invalid definitions so the flow continues without this experiment
          }
        }
        return acc;
      }, {});
    } catch {
      // API failed (e.g. CORS) or other error: run flow without experiments
    }
  }

  const gb = new GrowthBook({
    attributes: { path: router?.activePath, ...dc, ...attributes },
    getDeviceId: cookieName => Cookies.get(cookieName),
    persistDeviceId: (cookieName, deviceId) => Cookies.set(cookieName, deviceId, { expires: DEVICE_ID_COOKIE_AGE }),
    trackingCallback: (experiment, result, deviceId) => {
      pushToDataLayer(getGrowthbookTrackingEvent(experiment, result, deviceId));
    },
  });

  gb.initSync({ payload: { features: combinedFeatures } });

  // usage
  const configs = getConfigs(
    gb,
    defaultConfig,
    testConfigs,
    attributes?.disableExperiments,
    attributes?.forceTestIndex,
  );

  return {
    growthbook: gb,
    routes: configs?.routes,
    actions: configs?.actions,
    initialContext: configs?.initialContext,
  };
};
